import { OmniaConfig } from "../../config"
import { OmniaApiException } from "../../error"
import { msg, msgText, msgTmpl } from "../../i18n/messages"
import { OmniaPromise } from "../../promises"
import { str } from "../../strings"
import { CssClass, HtmlTemplateString, IUibModalScope, LocaleString, StringLike } from "../../types"
import { defineDirective } from "../../utils/angular"
import { isArray, isString } from "../../utils/utils"
import { IDialogScope, IMessageBox, IMessageBoxButton } from "../types"

interface Shell<Content = string> {
    showMessageBox(options: IMessageBox<Content>, staticBackdrop?: boolean): OmniaPromise<any>
}

export function showWarning<Content>(shell: Shell<Content>, content: Content, title?: StringLike, okText?: string, staticBackdrop: boolean = false) {

    const p = shell.showMessageBox({

        title,
        content,

        buttons: [
            { text: okText || msg(str.Ok), value: true, cssClass: "btn-warning" }
        ]

    }, staticBackdrop)

    return p.then(r => !!r)

}

export function showInfo<Content>(shell: Shell<Content>, content: Content, title?: StringLike, okText?: string) {

    const p = shell.showMessageBox({

        title,
        content,

        buttons: [
            { text: okText || msg(str.Ok), value: true, cssClass: "btn-info btn-min-width" }
        ]

    })

    return p.then(r => !!r)

}

export function showOkCancel<Content>(shell: Shell<Content>, content: Content, title?: StringLike, okText?: string, okClass: string = "btn-primary") {

    const p = shell.showMessageBox({

        title,
        content,

        buttons: [
            { text: msg(str.Cancel), value: false, cssClass: "btn-warning" },
            { text: okText || msg(str.Ok), value: true, cssClass: okClass }
        ]

    })

    return p.then(r => !!r)

}

export function showYesNo(shell: Shell, content: string, title?: StringLike, yesBtnText?: string, noBtnText?: string) {

    const p = shell.showMessageBox({

        title,
        content,

        buttons: [
            { text: noBtnText || msg(str.No), value: false, cssClass: "btn-warning" },
            { text: yesBtnText || msg(str.Yes), value: true, cssClass: "btn-primary" }
        ]

    })

    return p.then(r => !!r)

}

interface Message {
    error: boolean
    msg: string | string[]
}

export function showGenericError(shell: Shell, msgid: string, provider: (id: string) => Message, title?: string) {

    const msg = provider(msgid) || {
        error: true,
        msg: msgTmpl(`<p>An error has occurred during the operation (error code: <code>{{msgid}}</code>)`, { msgid })
    }

    const content = (isArray(msg.msg) ? msg.msg : [msg.msg]).map(m => `<p>${m}</p>`).join("")

    return msg.error
        ? showWarning(shell, content, title)
        : showInfo(shell, content, title)

}



export function showDeleteCancel<Content>(shell: Shell<Content>, content: Content, title?: StringLike, deleteText?: string) {

    const p = shell.showMessageBox({

        title,
        content,
        icon: "delete",

        buttons: [
            { text: msg(str.Cancel), value: false, cssClass: "btn-warning" },
            { text: deleteText || msg(str.Delete), value: true, cssClass: "btn-danger btn-min-width" }
        ]

    })

    return p.then(r => !!r)

}

export function showException(shell: Shell, exception: OmniaApiException, content: StringLike, title: string = str.Error) {

    if (exception?.Message) {

        const msgid = isString(content) ? content : content?.toString()

        const m1 = msg(msgid)

        const m2 = msg(
            `<p>The error returned by (@brand) was:</p> <p style='padding: 10px; border: solid 1px #ccc; border-radius: 3px; background-color: #eee;'>{{msg}}</p>`,
            { msg: exception.Message }
        )

        content = msgText([m1, m2].join("\n"))

    }

    return showWarning(shell, content, title)

}

export function showTextArea(shell: Shell, content: string, title = "Info" as LocaleString) {

    const form: HtmlTemplateString = `<form><textarea id='omnia-debug-text-area' style='width: 100%; font-family: monospace' rows='25'>${msg(content)}</textarea></form>`

    const p = shell.showMessageBox({

        title,
        content: msgText(form),

        buttons: [
            { text: msg(str.Ok), value: true, cssClass: "btn-info" }
        ]

    })

    return p.then(r => !!r)

}

export function showYesNoCancel(shell: Shell, content: string, title?: StringLike) {

    const p = shell.showMessageBox({

        title,
        content,

        buttons: [
            { text: msg(str.Cancel), value: null, cssClass: "btn-warning" },
            { text: msg(str.No), value: false, cssClass: "btn-default btn-min-width" as CssClass },
            { text: msg(str.Yes), value: true, cssClass: "btn-default btn-min-width" as CssClass }
        ]

    })

    return p

}

export function createMsgbox(): ng.IDirective {
    return defineDirective(MsgboxCtrl, "shared/shell/msgbox/msgbox.html")
}

export function createMsgboxWithTmpl(): ng.IDirective {
    const def = defineDirective(MsgboxCtrl, undefined)
    const template: HtmlTemplateString = "<div ng-include='vm.templateUrl'></div>"
    def.template = template
    return def
}

export class MsgboxCtrl {


    private modalScope: IUibModalScope
    public options: IMessageBox
    public title: StringLike
    public icon: string
    public content: StringLike
    public templateUrl: string
    public args: {}

    static $inject = ["$scope", "$sce", "omniaConfig"]

    constructor(
        $scope: IDialogScope,
        $sce: ng.ISCEService,
        config: OmniaConfig
    ) {

        this.modalScope = <IUibModalScope>($scope.$parent)
        const o = this.options = (<IDialogScope>this.modalScope.$parent).options

        this.templateUrl = o.templateUrl
        this.args = o.arguments
        this.title = o.title ?? config.brand.name
        this.icon = o.icon
        this.content = $sce.trustAsHtml(o.content)

    }

    getButtonCssClass(button: IMessageBoxButton) {
        return (button.cssClass || ("btn-default btn-min-width" as CssClass))
    }

    return(button: IMessageBoxButton) {
        this.modalScope.$close(button.value)
    }

    close(value: any) {
        this.modalScope.$close(value)
    }


}
